import { useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MOCK_VESSELS, MOCK_CREW, resolveClientKey, getClientVessels, getClientCrew } from "@/data/portalMockData";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { Users, Shield, Calendar, ChevronDown, ChevronUp } from "lucide-react";

const ragClass = (status: string) => status === "valid" ? "bg-green-100 text-green-700" : status === "expiring" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

const CrewPersonnel = () => {
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);
  const clientVessels = clientKey ? getClientVessels(clientKey) : MOCK_VESSELS;
  const clientCrew = clientKey ? getClientCrew(clientKey) : MOCK_CREW;
  const [selectedVessel, setSelectedVessel] = useState(clientVessels[0]?.name || "");
  const [expandedCrew, setExpandedCrew] = useState<string | null>(null);

  const vesselCrew = clientCrew.filter(c => c.vessel === selectedVessel);
  const certTypes = ["STCW", "Medical", "Flag Endorsement"];

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Personnel</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Crew & Personnel</h1>
      </div>

      <Tabs defaultValue="manifest" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="manifest">Crew Manifest</TabsTrigger>
          <TabsTrigger value="changes">Crew Changes</TabsTrigger>
          <TabsTrigger value="matrix">Cert Matrix</TabsTrigger>
        </TabsList>

        <TabsContent value="manifest">
          <div className="mb-4">
            <Select value={selectedVessel} onValueChange={setSelectedVessel}>
              <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
              <SelectContent>{clientVessels.map(v => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            {vesselCrew.length === 0 ? (
              <div className="portal-card p-8 text-center"><p className="text-sm text-[hsl(215,12%,50%)]">No crew assigned to this vessel.</p></div>
            ) : vesselCrew.map(crew => (
              <div key={crew.id} className="portal-card overflow-hidden">
                <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedCrew(expandedCrew === crew.id ? null : crew.id)}>
                  <div className="w-10 h-10 rounded-full bg-[hsl(211,100%,40%)]/10 flex items-center justify-center text-sm font-bold text-[hsl(211,100%,40%)]">
                    {crew.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-[hsl(215,25%,17%)]">{crew.name}</p>
                    <p className="text-xs text-[hsl(215,12%,50%)]">{crew.role} · {crew.nationality}</p>
                  </div>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${ragClass(crew.certStatus)}`}>{crew.certStatus}</span>
                  {expandedCrew === crew.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
                {expandedCrew === crew.id && (
                  <div className="px-4 pb-4 bg-[hsl(220,20%,97%)] border-t border-[hsl(220,15%,90%)]">
                    <p className="text-xs text-[hsl(215,12%,50%)] mt-3 mb-3">{crew.bio}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div><span className="text-[hsl(215,12%,50%)]">On Board Since:</span><br/><span className="font-medium">{crew.onBoardSince}</span></div>
                      <div><span className="text-[hsl(215,12%,50%)]">Sign-Off:</span><br/><span className="font-medium">{crew.scheduledSignOff}</span></div>
                      <div><span className="text-[hsl(215,12%,50%)]">CTV Experience:</span><br/><span className="font-medium">{crew.yearsCtv} years</span></div>
                      <div><span className="text-[hsl(215,12%,50%)]">STCW Expiry:</span><br/><span className="font-medium">{crew.stcwExpiry}</span></div>
                      <div><span className="text-[hsl(215,12%,50%)]">Medical Expiry:</span><br/><span className="font-medium">{crew.medicalExpiry}</span></div>
                      <div><span className="text-[hsl(215,12%,50%)]">Flag Endorsement:</span><br/><span className="font-medium">{crew.flagEndorsementExpiry}</span></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="changes">
          <div className="portal-card">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                  {["Date", "Vessel", "Outgoing", "Incoming", "Role", "Reason"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  <tr className="border-b border-[hsl(220,15%,92%)]">
                    <td className="px-4 py-2.5">2025-04-10</td><td className="px-4 py-2.5">Velocity Endeavour</td>
                    <td className="px-4 py-2.5">Capt. J. Singh</td><td className="px-4 py-2.5">Capt. D. Ramirez</td>
                    <td className="px-4 py-2.5">Master</td><td className="px-4 py-2.5">Scheduled rotation</td>
                  </tr>
                  <tr className="border-b border-[hsl(220,15%,92%)]">
                    <td className="px-4 py-2.5">2025-04-15</td><td className="px-4 py-2.5">Velocity Pioneer</td>
                    <td className="px-4 py-2.5">Capt. R. Maharaj</td><td className="px-4 py-2.5">Capt. L. Thomas</td>
                    <td className="px-4 py-2.5">Master</td><td className="px-4 py-2.5">Scheduled rotation</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="matrix">
          <div className="portal-card overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                <th className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">Crew Member</th>
                <th className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">Vessel</th>
                {certTypes.map(ct => <th key={ct} className="px-4 py-2.5 text-center font-medium text-[hsl(215,12%,50%)]">{ct}</th>)}
              </tr></thead>
              <tbody>
                {clientCrew.map(c => {
                  const stcwStatus = new Date(c.stcwExpiry) < new Date() ? "expired" : new Date(c.stcwExpiry) < new Date(Date.now() + 90*86400000) ? "expiring" : "valid";
                  const medStatus = new Date(c.medicalExpiry) < new Date() ? "expired" : new Date(c.medicalExpiry) < new Date(Date.now() + 90*86400000) ? "expiring" : "valid";
                  const flagStatus = new Date(c.flagEndorsementExpiry) < new Date() ? "expired" : "valid";
                  return (
                    <tr key={c.id} className="border-b border-[hsl(220,15%,92%)]">
                      <td className="px-4 py-2.5 font-medium">{c.name}</td>
                      <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{c.vessel}</td>
                      {[stcwStatus, medStatus, flagStatus].map((s, i) => (
                        <td key={i} className="px-4 py-2.5 text-center">
                          <span className={`inline-block w-3 h-3 rounded-full ${s === "valid" ? "bg-[#28A745]" : s === "expiring" ? "bg-[#FFC107]" : "bg-[#DC3545]"}`} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default CrewPersonnel;
